import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Upload, Trash2, Star, Image as ImageIcon, Video, FileText, Loader2, X, Check, CircleAlert, ArrowLeft, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/use-auth";
import { useSubscription } from "@/lib/use-subscription";
import { listMyMedia, registerMedia, deleteMedia, setCoverMedia } from "@/lib/chef-media.functions";
import { PHOTO_LIMITS, tierFromChef, type PremiumTier } from "@/lib/chef-utils";
import { validateUpload, acceptAttribute, type UploadKind } from "@/lib/upload-limits";
import { uploadFileWithProgress } from "@/lib/upload-with-progress";
import { friendlyError } from "@/lib/friendly-error";
import { requireVerifiedChef } from "@/lib/require-verified-chef";

const TIER_LABEL: Record<PremiumTier, string> = {
  free: "Gratuito",
  silver: "Premium Silver",
  gold: "Premium Gold",
};

export const Route = createFileRoute("/_authenticated/dashboard/foto")({
  head: () => ({ meta: [{ title: "Media — ChefConnect" }] }),
  beforeLoad: requireVerifiedChef,
  component: MediaPage,
});

const BUCKET: Record<UploadKind & ("photo" | "video" | "menu_pdf"), string> = {
  photo: "chef-photos",
  video: "chef-videos",
  menu_pdf: "chef-menus",
};
const ACCEPT: Record<"photo" | "video" | "menu_pdf", string> = {
  photo: acceptAttribute("photo"),
  video: acceptAttribute("video"),
  menu_pdf: acceptAttribute("menu_pdf"),
};

type UploadItem = {
  id: string;
  name: string;
  size: number;
  kind: "photo" | "video" | "menu_pdf";
  status: "pending" | "uploading" | "done" | "error";
  progress: number;
  preview?: string;
  error?: string;
  controller: AbortController;
};

function MediaPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const listFn = useServerFn(listMyMedia);
  const regFn = useServerFn(registerMedia);
  const delFn = useServerFn(deleteMedia);
  const coverFn = useServerFn(setCoverMedia);

  const mediaQ = useQuery({ queryKey: ["my-media"], queryFn: () => listFn() });
  const { isPremium } = useSubscription();
  // Sprint 19.1 — il tier è fonte di verità per limiti foto e marker.
  const chefQ = useQuery({
    enabled: !!user,
    queryKey: ["my-chef-tier", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("chefs")
        .select("premium_tier, premium_until")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data;
    },
  });
  const tier: PremiumTier = tierFromChef({
    premium_tier: chefQ.data?.premium_tier ?? null,
    premium_until: chefQ.data?.premium_until ?? null,
  });
  const photoLimit = PHOTO_LIMITS[tier];
  const items = mediaQ.data ?? [];
  const photos = items.filter((m) => m.kind === "photo");
  const videos = items.filter((m) => m.kind === "video");
  const menus = items.filter((m) => m.kind === "menu_pdf");
  const hiddenPhotos = Math.max(0, photos.length - photoLimit);

  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const updateUpload = (id: string, patch: Partial<UploadItem>) =>
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  const removeUpload = (id: string) =>
    setUploads((prev) => {
      const u = prev.find((x) => x.id === id);
      if (u?.preview) URL.revokeObjectURL(u.preview);
      return prev.filter((x) => x.id !== id);
    });

  const delMut = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Eliminato");
      qc.invalidateQueries({ queryKey: ["my-media"] });
      qc.invalidateQueries({ queryKey: ["my-onboarding"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const coverMut = useMutation({
    mutationFn: (id: string) => coverFn({ data: { id } }),
    onSuccess: () => { toast.success("Copertina aggiornata"); qc.invalidateQueries({ queryKey: ["my-media"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  async function handleUpload(kind: "photo" | "video" | "menu_pdf", file: File) {
    if (!user) return;
    const id = crypto.randomUUID();
    const controller = new AbortController();
    const item: UploadItem = {
      id, name: file.name, size: file.size, kind,
      status: "pending", progress: 0,
      preview: kind === "photo" ? URL.createObjectURL(file) : undefined,
      controller,
    };
    setUploads((p) => [...p, item]);

    // Pre-validazione client (identica al server).
    const pre = validateUpload(kind, { size: file.size, type: file.type });
    if (!pre.ok) {
      updateUpload(id, { status: "error", error: pre.message });
      return;
    }
    try {
      updateUpload(id, { status: "uploading", progress: 0 });
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "bin";
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
      await uploadFileWithProgress({
        bucket: BUCKET[kind],
        path,
        file,
        onProgress: (pct) => updateUpload(id, { progress: pct }),
        signal: controller.signal,
      });
      await regFn({ data: { storage_path: path, kind, title: file.name } });
      updateUpload(id, { status: "done", progress: 100 });
      qc.invalidateQueries({ queryKey: ["my-media"] });
      qc.invalidateQueries({ queryKey: ["my-onboarding"] });
      // Rimuovi dopo qualche secondo per non lasciare la lista piena.
      setTimeout(() => removeUpload(id), 2500);
    } catch (e) {
      const aborted = (e as { name?: string })?.name === "AbortError";
      updateUpload(id, {
        status: "error",
        error: aborted ? "Caricamento annullato." : friendlyError(e, "Caricamento non riuscito. Riprova."),
      });
    }
  }

  return (
    <div className="container mx-auto max-w-5xl px-4 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Torna alla dashboard
        </Link>
        <Button
          variant="outline"
          size="sm"
          disabled={uploads.some((u) => u.status === "uploading" || u.status === "pending")}
          onClick={async () => {
            const inFlight = uploads.filter((u) => u.status === "uploading" || u.status === "pending");
            if (inFlight.length > 0) {
              toast.message("Attendi il completamento dei caricamenti in corso…");
              return;
            }
            const failed = uploads.some((u) => u.status === "error");
            if (failed) {
              toast.error("Alcuni file non sono stati caricati. Risolvi o chiudi gli errori prima di uscire.");
              return;
            }
            navigate({ to: "/dashboard" });
          }}
        >
          <LogOut className="mr-1.5 h-4 w-4" /> Salva ed esci
        </Button>
      </div>
      <h1 className="mt-3 font-display text-4xl">Foto, video e menu</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Piano: <strong>{TIER_LABEL[tier]}</strong> · {photos.length}/{photoLimit} foto pubbliche
        {tier === "free" && " · video e menu PDF riservati a Premium"}
      </p>
      {hiddenPhotos > 0 && (
        <div className="mt-3 rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          {hiddenPhotos} {hiddenPhotos === 1 ? "foto è nascosta" : "foto sono nascoste"} dal tuo profilo pubblico perché superano il limite del piano {TIER_LABEL[tier]}. Le foto restano salvate e tornano automaticamente visibili se passi a un piano superiore.
        </div>
      )}

      {uploads.length > 0 && (
        <div className="mt-6 space-y-2 rounded-lg border border-border bg-card p-4">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Caricamenti</p>
          {uploads.map((u) => (
            <div key={u.id} className="flex items-center gap-3 rounded-md border border-border/60 bg-background p-2">
              {u.preview ? (
                <img src={u.preview} alt="" className="h-10 w-10 rounded object-cover" />
              ) : (
                <div className="grid h-10 w-10 place-items-center rounded bg-secondary text-muted-foreground">
                  {u.kind === "video" ? <Video className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{u.name}</p>
                {u.status === "uploading" && <Progress value={u.progress} className="mt-1 h-1.5" />}
                {u.status === "error" && <p className="mt-0.5 text-xs text-destructive">{u.error}</p>}
                {u.status === "done" && <p className="mt-0.5 text-xs text-emerald-600 dark:text-emerald-400">Caricamento completato</p>}
                {u.status === "pending" && <p className="mt-0.5 text-xs text-muted-foreground">In attesa…</p>}
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {u.status === "uploading" && (
                  <>
                    <span>{u.progress}%</span>
                    <Button size="icon" variant="ghost" aria-label="Annulla" onClick={() => u.controller.abort()}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
                {u.status === "done" && <Check className="h-4 w-4 text-emerald-600" />}
                {u.status === "error" && (
                  <>
                    <CircleAlert className="h-4 w-4 text-destructive" />
                    <Button size="sm" variant="ghost" onClick={() => removeUpload(u.id)}>Chiudi</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="photos" className="mt-8">
        <TabsList>
          <TabsTrigger value="photos"><ImageIcon className="mr-1.5 h-4 w-4" /> Foto</TabsTrigger>
          <TabsTrigger value="videos"><Video className="mr-1.5 h-4 w-4" /> Video</TabsTrigger>
          <TabsTrigger value="menus"><FileText className="mr-1.5 h-4 w-4" /> Menu PDF</TabsTrigger>
        </TabsList>

        <TabsContent value="photos" className="mt-6 space-y-4">
          <UploadButton
            kind="photo"
            disabled={photos.length >= photoLimit}
            onFile={(f) => handleUpload("photo", f)}
          />
          {photos.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
              <ImageIcon className="mx-auto h-10 w-10 text-muted-foreground" />
              <h3 className="mt-3 font-display text-xl">Nessuna foto caricata</h3>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                Le foto sono il primo elemento che vede un cliente. Carica almeno 3 immagini di alta qualità dei tuoi piatti per renderle visibili sul profilo.
              </p>
              <p className="mx-auto mt-3 max-w-md text-xs text-muted-foreground">
                Suggerimento: foto luminose, piatti centrati, niente filtri pesanti.
              </p>
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {photos.map((m) => (
              <figure key={m.id} className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-secondary">
                <img src={m.url} alt={m.title ?? ""} className="h-full w-full object-cover" />
                {m.is_cover && (
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-gold px-2 py-0.5 text-[10px] font-semibold text-gold-foreground">
                    <Star className="h-3 w-3 fill-current" /> Cover
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 flex translate-y-full justify-end gap-1 bg-gradient-to-t from-black/80 to-transparent p-2 transition group-hover:translate-y-0">
                  {!m.is_cover && (
                    <Button size="sm" variant="secondary" onClick={() => coverMut.mutate(m.id)}>Cover</Button>
                  )}
                  <Button size="sm" variant="destructive" onClick={() => delMut.mutate(m.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </figure>
            ))}
          </div>
          )}
        </TabsContent>

        <TabsContent value="videos" className="mt-6 space-y-4">
          {!isPremium && <PremiumGate label="Video" />}
          {isPremium && (
            <>
              <UploadButton kind="video" disabled={false} onFile={(f) => handleUpload("video", f)} />
              {videos.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
                  <Video className="mx-auto h-10 w-10 text-muted-foreground" />
                  <h3 className="mt-3 font-display text-xl">Nessun video</h3>
                  <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                    Un breve video di presentazione (30–60 secondi) può raddoppiare le richieste. Mostra la tua cucina dal vivo o un piatto signature.
                  </p>
                </div>
              ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {videos.map((m) => (
                  <div key={m.id} className="relative overflow-hidden rounded-lg border border-border">
                    <video src={m.url} controls className="aspect-video w-full bg-black" />
                    <Button size="sm" variant="destructive" className="absolute right-2 top-2" onClick={() => delMut.mutate(m.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="menus" className="mt-6 space-y-4">
          {!isPremium && <PremiumGate label="Menu PDF" />}
          {isPremium && (
            <>
              <UploadButton kind="menu_pdf" disabled={false} onFile={(f) => handleUpload("menu_pdf", f)} />
              {menus.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
                  <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
                  <h3 className="mt-3 font-display text-xl">Nessun menù caricato</h3>
                  <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                    Carica i tuoi menù in PDF: i clienti potranno scaricarli direttamente dal tuo profilo prima di contattarti.
                  </p>
                </div>
              ) : (
              <ul className="divide-y divide-border rounded-lg border border-border bg-card">
                {menus.map((m) => (
                  <li key={m.id} className="flex items-center justify-between gap-3 p-4">
                    <a href={m.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 truncate text-sm font-medium hover:underline">
                      <FileText className="h-4 w-4 text-gold" /> {m.title ?? "Menu"}
                    </a>
                    <Button size="sm" variant="ghost" onClick={() => delMut.mutate(m.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UploadButton({
  kind, disabled, onFile,
}: { kind: "photo" | "video" | "menu_pdf"; disabled: boolean; onFile: (f: File) => Promise<void> }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const allowMultiple = kind === "photo";
  return (
    <div>
      <input
        ref={ref}
        type="file"
        multiple={allowMultiple}
        accept={ACCEPT[kind]}
        className="hidden"
        onChange={async (e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length === 0) return;
          setBusy(true);
          for (const f of files) {
            await onFile(f);
          }
          setBusy(false);
          if (ref.current) ref.current.value = "";
        }}
      />
      <Button
        onClick={() => ref.current?.click()}
        disabled={disabled || busy}
        className="bg-gold text-gold-foreground hover:bg-gold/90"
      >
        {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}
        Carica
      </Button>
      {disabled && !busy && (
        <p className="mt-2 text-xs text-muted-foreground">
          Limite raggiunto. <a href="/premium" className="font-medium underline">Passa a Premium</a> per caricamenti illimitati.
        </p>
      )}
    </div>
  );
}

function PremiumGate({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-gold/40 bg-gold-muted/20 p-6 text-center">
      <p className="font-display text-2xl">🔒 {label} riservati a Premium</p>
      <p className="mt-2 text-sm text-muted-foreground">Sblocca {label.toLowerCase()} illimitati con il piano Premium.</p>
      <Button asChild className="mt-4 bg-gold text-gold-foreground hover:bg-gold/90">
        <a href="/premium">Scopri Premium</a>
      </Button>
    </div>
  );
}